import React from "react";
import { Badge } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { IconExternalLink } from "@tabler/icons-react";
import { EvalResult, TurnResult } from "../../services/evals";
import { TurnResultCard } from "./TurnResultCard";
import { getLangfuseTraceUrl } from "../utils";

interface ExpandedResultRowProps {
  result: EvalResult;
}

export const ExpandedResultRow: React.FC<ExpandedResultRowProps> = ({ result }) => {
  console.log("[ExpandedResultRow] Rendering with result:", {
    id: result.id,
    test_case_name: result.test_case_name,
    turn_results_type: typeof result.turn_results,
    turn_results_length: result.turn_results?.length,
    turn_results_sample: result.turn_results?.[0]
      ? {
          turn_number: result.turn_results[0].turn_number,
          has_evaluation: !!result.turn_results[0].evaluation,
          actual_ai_response_type: typeof result.turn_results[0].actual_ai_response,
        }
      : "no turns",
  });
  const getScoreClass = (score: number) => {
    if (score >= 4) return "!al-bg-green-100 !al-text-green-700 al-border al-border-green-200";
    if (score >= 3) return "!al-bg-yellow-100 !al-text-yellow-700 al-border al-border-yellow-200";
    return "!al-bg-red-100 !al-text-red-700 al-border al-border-red-200";
  };

  const getStatusLabel = (score: number) => {
    if (score >= 4) return "Pass";
    if (score >= 3) return "Ok";
    return "Fail";
  };

  const scoreClass = getScoreClass(result.overall_score);

  return (
    <tr>
      <td colSpan={7} className="al-p-0">
        <div className="al-bg-gray-50 al-border-t al-border-b al-border-gray-200 al-max-h-[70vh] al-overflow-y-auto al-overflow-x-hidden">
          <div className="al-p-6 al-max-w-full">
            <div className="al-mb-6">
              {result.test_case_description && (
                <div className="al-text-sm al-text-gray-600 al-mb-3">
                  {result.test_case_description}
                </div>
              )}
              {result.test_case_tags && result.test_case_tags.length > 0 && (
                <Stack direction="row" align="center" gap="sm" className="al-mb-3">
                  <span className="al-text-xs al-font-semibold al-text-cyan-700">Tags:</span>
                  <Stack direction="row" gap="xs" className="al-flex-wrap">
                    {result.test_case_tags.map((tag, idx) => (
                      <Badge
                        key={idx}
                        className="al-text-[11px] al-px-2 al-py-0 !al-bg-cyan-100 !al-text-cyan-700 al-border al-border-cyan-200"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </Stack>
                </Stack>
              )}
              <Stack direction="row" align="center" gap="lg" className="al-flex-wrap">
                <div className="al-text-sm al-text-gray-600">Overall Score:</div>
                <Badge className={`${scoreClass} al-text-base`}>
                  {Math.round(result.overall_score)}/5
                </Badge>
                <Badge className={scoreClass}>{getStatusLabel(result.overall_score)}</Badge>
                {result.trace_id && (
                  <a
                    href={getLangfuseTraceUrl(result.trace_id)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="al-inline-flex al-items-center al-gap-1 al-text-sm al-text-blue-600 hover:al-text-blue-800 hover:al-underline"
                    onClick={e => e.stopPropagation()}
                  >
                    <IconExternalLink className="al-w-4 al-h-4" />
                    View Trace in Langfuse
                  </a>
                )}
              </Stack>
            </div>
            <div className="al-space-y-4 al-max-w-full">
              {result.turn_results?.map((turn: TurnResult, index: number) => (
                <TurnResultCard key={index} turn={turn} variant="compact" />
              ))}
            </div>
          </div>
        </div>
      </td>
    </tr>
  );
};
